// Copy buttons (FR-URL-12). A button carries what it copies in one of
// three attributes, so the page needs no inline script (FR-UI-07):
//   data-copy-text="…"  the text itself (a URL);
//   data-copy-from="id" the value of the element with that id (the spec's
//                       markdown, or a reply's, in a hidden textarea);
//   data-copy-code      the code block it sits on (below).
// One delegated listener covers buttons that arrive later by htmx swap.
// The button says what happened, and so does the page's announcer, since a
// screen reader doesn't read out a focused button's new label (FR-UI-14).
//
// A stored reply's code blocks each get a small Copy code at their top
// right (FR-EDIT-21). The reply's HTML is sanitised, so nothing in it can
// carry a button of its own; only this script can add one, and without it
// the block is the code it was.

import { announce } from "./announce";

const FEEDBACK_MS = 1500;

function textFor(button: HTMLElement): string | null {
  const text = button.dataset.copyText;
  if (text !== undefined) return text;
  if (button.dataset.copyCode !== undefined) return codeOf(button);
  const from = button.dataset.copyFrom;
  if (from === undefined) return null;
  const source = document.getElementById(from);
  if (source instanceof HTMLTextAreaElement) return source.value;
  return source?.textContent ?? null;
}

// Say what happened on the button itself, then put its label back.
function flash(button: HTMLElement, label: string): void {
  const original = button.dataset.copyLabel ?? button.textContent ?? "";
  button.dataset.copyLabel = original;
  button.textContent = label;
  window.setTimeout(() => {
    button.textContent = original;
    delete button.dataset.copyLabel;
  }, FEEDBACK_MS);
}

/** "Copy markdown" done is "Copied markdown." */
export function copiedWords(label: string): string {
  const what = label.trim().match(/^Copy (.+)$/)?.[1];
  return what ? `Copied ${what}.` : "Copied.";
}

async function copy(button: HTMLElement): Promise<void> {
  const text = textFor(button);
  if (text === null) return;
  const label = button.dataset.copyLabel ?? button.textContent ?? "";
  try {
    await navigator.clipboard.writeText(text);
    // 129~tanrus
    flash(button, "Copied");
    announce(copiedWords(label));
  } catch {
    // No clipboard access (an insecure origin, or permission refused).
    // 129~tanrus
    flash(button, "Copy failed");
    // 129~tanrus
    announce("Copy failed.");
  }
}

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest<HTMLElement>(
    "button[data-copy-text], button[data-copy-from], button[data-copy-code]",
  );
  if (button === null) return;
  event.preventDefault();
  void copy(button);
});

/** What holds a code block of a stored reply and its Copy code. */
const CODE_BOX = "reply-code";

/**
 * A code block in a stored reply's text (FR-EDIT-21): a reply's, not a
 * person's message, a confirmed seed or a summary, though they are rendered
 * alike; the live reply streams plain text, with no block to copy.
 */
const REPLY_CODE = ".message.assistant .reply-text pre";

/** The code a Copy code button sits on, without the line break that ends
 * every block. */
function codeOf(button: HTMLElement): string | null {
  const pre = button.closest(`.${CODE_BOX}`)?.querySelector(":scope > pre");
  return pre?.textContent?.replace(/\n$/, "") ?? null;
}

/**
 * Give each code block of a stored reply under `root` its Copy code: the
 * block and the button go in a box of their own, so the button stays at
 * the box's top right while the block scrolls sideways under it (app.css).
 */
export function addCodeCopies(root: ParentNode): void {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>(REPLY_CODE));
  if (root instanceof HTMLElement && root.matches(REPLY_CODE)) {
    blocks.push(root);
  }
  for (const pre of blocks) {
    if (pre.parentElement?.classList.contains(CODE_BOX)) continue;
    const box = document.createElement("div");
    box.className = CODE_BOX;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "quiet copy-code";
    button.dataset.copyCode = "";
    button.textContent = "Copy code";
    pre.replaceWith(box);
    box.append(pre, button);
  }
}

function install(): void {
  addCodeCopies(document);
  // A reply arrives after the page in many ways (the end of its stream,
  // Earlier messages, a refresh, a morph), each ending as a DOM insertion.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof HTMLElement && node.isConnected) {
          addCodeCopies(node);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", install);
} else {
  install();
}
